import React, { useEffect, useRef, useState, useCallback } from 'react';
import 'ol/ol.css';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import XYZ from 'ol/source/XYZ';
import Draw from 'ol/interaction/Draw';
import Modify from 'ol/interaction/Modify';
import GeoJSON from 'ol/format/GeoJSON';
import { fromLonLat, toLonLat } from 'ol/proj';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';

const geojsonFormat = new GeoJSON({
  dataProjection: 'EPSG:4326',
  featureProjection: 'EPSG:3857',
});

const editableStyle = new Style({
  fill: new Fill({ color: 'rgba(99, 8, 1, 0.2)' }),
  stroke: new Stroke({ color: '#630801', width: 2 }),
});

const referenceStyle = new Style({
  fill: new Fill({ color: 'rgba(100, 100, 100, 0.1)' }),
  stroke: new Stroke({ color: '#999', width: 1, lineDash: [4, 4] }),
});

// Small and plain. These are reference, not the subject: you are dragging the
// outline and watching which dots fall inside it, so a pin that competes with
// the boundary for attention is working against the task. Unclustered for the
// same reason, since a cluster hides exactly the in-or-out question being
// asked at the edge.
// The blob being drawn, before it is applied. Dashed and hollow so it never
// reads as part of the boundary.
const scratchStyle = new Style({
  fill: new Fill({ color: 'rgba(30, 90, 160, 0.15)' }),
  stroke: new Stroke({ color: '#1e5aa0', width: 2, lineDash: [6, 4] }),
});

const pinStyle = new Style({
  image: new CircleStyle({
    radius: 3.5,
    fill: new Fill({ color: 'rgba(103, 8, 1, 0.75)' }),
    stroke: new Stroke({ color: '#ffffff', width: 1 }),
  }),
});

export default function PolygonEditor({
  boundary, onBoundaryChange, referenceBoundaries = [], pins = [],
  referenceLabel = 'Gray dashed outlines are shown for reference.',
  downloadName = 'boundary.geojson',
  // When given, drawing becomes a boolean operation against the existing
  // shape instead of dropping a second polygon beside it. Called with
  // (areaGeoJson, 'add' | 'remove'); the caller is expected to hand back a new
  // `boundary` prop.
  onReshape = null,
}) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const editSourceRef = useRef(null);
  const referenceSourceRef = useRef(null);
  const pinSourceRef = useRef(null);
  const drawInteractionRef = useRef(null);
  const scratchSourceRef = useRef(null);
  const onReshapeRef = useRef(onReshape);
  // Set while the editor itself is the source of a boundary change, so the
  // sync effect below does not fight a drag in progress.
  const selfEditRef = useRef(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawMode, setDrawMode] = useState(null);
  const fileInputRef = useRef(null);

  const syncBoundary = useCallback(() => {
    if (!editSourceRef.current) return;
    const features = editSourceRef.current.getFeatures();
    if (features.length === 0) {
      onBoundaryChange(null);
      return;
    }

    // Merge all features into a single MultiPolygon
    const allCoords = [];
    for (const feature of features) {
      const geom = feature.getGeometry();
      if (!geom) continue;
      const geojson = JSON.parse(geojsonFormat.writeGeometry(geom));
      if (geojson.type === 'Polygon') {
        allCoords.push(geojson.coordinates);
      } else if (geojson.type === 'MultiPolygon') {
        allCoords.push(...geojson.coordinates);
      }
    }

    if (allCoords.length === 0) {
      onBoundaryChange(null);
      return;
    }

    onBoundaryChange({
      type: 'MultiPolygon',
      coordinates: allCoords,
    });
  }, [onBoundaryChange]);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstance.current) return;

    const editSource = new VectorSource();
    editSourceRef.current = editSource;

    const referenceSource = new VectorSource();
    referenceSourceRef.current = referenceSource;

    const pinSource = new VectorSource();
    pinSourceRef.current = pinSource;

    // Drawing lands here, never in editSource. A drawn blob is an instruction
    // ("add this", "take this out"), not a second polygon to sit beside the
    // region: appending one produced overlapping rings and spikes, because
    // collecting features into a MultiPolygon is a collection, not a union.
    const scratchSource = new VectorSource();
    scratchSourceRef.current = scratchSource;

    const map = new Map({
      target: mapRef.current,
      layers: [
        new TileLayer({
          source: new XYZ({
            url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
            attributions: '&copy; OpenStreetMap contributors',
          }),
        }),
        new VectorLayer({
          source: referenceSource,
          style: referenceStyle,
        }),
        new VectorLayer({
          source: pinSource,
          style: pinStyle,
        }),
        new VectorLayer({
          source: scratchSource,
          style: scratchStyle,
        }),
        // The editable shape sits on top, so its vertices stay grabbable even
        // where pins are dense.
        new VectorLayer({
          source: editSource,
          style: editableStyle,
        }),
      ],
      view: new View({
        center: fromLonLat([-98.5, 39.8]), // Center of US
        zoom: 4,
      }),
    });

    // Add modify interaction
    const modify = new Modify({ source: editSource });
    modify.on('modifyend', () => { selfEditRef.current = true; syncBoundary(); });
    map.addInteraction(modify);

    mapInstance.current = map;

    // Load existing boundary
    if (boundary) {
      try {
        const features = geojsonFormat.readFeatures(boundary);
        editSource.addFeatures(features);
        if (features.length > 0) {
          const extent = editSource.getExtent();
          map.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 12 });
        }
      } catch (e) {
        console.error('Error loading boundary:', e);
      }
    }

    return () => {
      map.setTarget(null);
      mapInstance.current = null;
    };
  }, []); // eslint-disable-line

  useEffect(() => { onReshapeRef.current = onReshape; }, [onReshape]);

  // The map only loaded `boundary` on mount, so a shape replaced from outside
  // (a reshape coming back from the server, a re-seed) never reached the
  // canvas. Redraw when it changes, unless this editor was the one that
  // changed it, which would yank the geometry out from under a drag.
  useEffect(() => {
    if (!editSourceRef.current || !mapInstance.current) return;
    if (selfEditRef.current) { selfEditRef.current = false; return; }

    editSourceRef.current.clear();
    if (!boundary) return;
    try {
      const features = geojsonFormat.readFeatures(boundary);
      editSourceRef.current.addFeatures(features);
    } catch (e) {
      console.error('Error loading boundary:', e); // eslint-disable-line no-console
    }
  }, [boundary]);

  useEffect(() => {
    if (!pinSourceRef.current) return;
    pinSourceRef.current.clear();
    pinSourceRef.current.addFeatures(
      pins
        .filter((p) => typeof p.lng === 'number' && typeof p.lat === 'number')
        .map((p) => {
          const f = new Feature({ geometry: new Point(fromLonLat([p.lng, p.lat])) });
          f.set('name', p.name);
          return f;
        }),
    );
  }, [pins]);

  // Update reference boundaries
  useEffect(() => {
    if (!referenceSourceRef.current) return;
    referenceSourceRef.current.clear();
    for (const ref of referenceBoundaries) {
      if (!ref.boundary) continue;
      try {
        const features = geojsonFormat.readFeatures(ref.boundary);
        referenceSourceRef.current.addFeatures(features);
      } catch {
        // Skip invalid geometries
      }
    }
  }, [referenceBoundaries]);

  // Draw a blob and apply it as a boolean against the current shape.
  const startDrawing = (mode = 'replace') => {
    if (!mapInstance.current || isDrawing) return;

    const reshaping = mode === 'add' || mode === 'remove';
    const draw = new Draw({
      source: reshaping ? scratchSourceRef.current : editSourceRef.current,
      type: 'Polygon',
    });

    draw.on('drawend', (evt) => {
      if (reshaping) {
        const area = JSON.parse(geojsonFormat.writeGeometry(evt.feature.getGeometry()));
        // Cleared immediately: the blob was an instruction, and the answer
        // arrives as a new boundary prop.
        setTimeout(() => {
          scratchSourceRef.current.clear();
          if (onReshapeRef.current) onReshapeRef.current(area, mode);
          stopDrawing();
        }, 50);
        return;
      }
      setTimeout(() => {
        selfEditRef.current = true;
        syncBoundary();
        stopDrawing();
      }, 100);
    });

    mapInstance.current.addInteraction(draw);
    drawInteractionRef.current = draw;
    setIsDrawing(true);
    setDrawMode(mode);
  };

  const stopDrawing = () => {
    if (!mapInstance.current || !drawInteractionRef.current) return;
    mapInstance.current.removeInteraction(drawInteractionRef.current);
    drawInteractionRef.current = null;
    setIsDrawing(false);
    setDrawMode(null);
  };

  const clearAll = () => {
    if (!editSourceRef.current) return;
    editSourceRef.current.clear();
    onBoundaryChange(null);
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const geojson = JSON.parse(evt.target.result);
        const features = geojsonFormat.readFeatures(geojson);
        if (features.length === 0) {
          alert('No polygon features found in file.');
          return;
        }
        editSourceRef.current.clear();
        editSourceRef.current.addFeatures(features);

        // Zoom to imported features
        const extent = editSourceRef.current.getExtent();
        mapInstance.current.getView().fit(extent, { padding: [50, 50, 50, 50], maxZoom: 12 });

        syncBoundary();
      } catch (err) {
        console.error('Error importing GeoJSON:', err);
        alert('Failed to parse GeoJSON file.');
      }
    };
    reader.readAsText(file);
    // Reset file input so same file can be re-selected
    e.target.value = '';
  };

  const exportGeoJSON = () => {
    if (!editSourceRef.current) return;
    const features = editSourceRef.current.getFeatures();
    if (features.length === 0) {
      alert('No features to export.');
      return;
    }
    const geojson = geojsonFormat.writeFeaturesObject(features);
    const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = downloadName;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {/* With onReshape, a drawn blob is applied as a union or a difference
            against the existing shape. Without it, drawing adds a polygon, which
            is what the appellation editor wants. */}
        {onReshape ? (
          <>
            <button
              type="button"
              onClick={() => (drawMode === 'add' ? stopDrawing() : startDrawing('add'))}
              className={`px-3 py-1.5 text-xs font-medium rounded-md border ${
                drawMode === 'add'
                  ? 'bg-green-50 text-green-800 border-green-300'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {drawMode === 'add' ? 'Cancel' : 'Add an area'}
            </button>
            <button
              type="button"
              onClick={() => (drawMode === 'remove' ? stopDrawing() : startDrawing('remove'))}
              className={`px-3 py-1.5 text-xs font-medium rounded-md border ${
                drawMode === 'remove'
                  ? 'bg-red-50 text-red-700 border-red-300'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {drawMode === 'remove' ? 'Cancel' : 'Remove an area'}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={isDrawing ? stopDrawing : () => startDrawing('replace')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md border ${
              isDrawing
                ? 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {isDrawing ? 'Stop Drawing' : 'Draw Polygon'}
          </button>
        )}
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="px-3 py-1.5 text-xs font-medium rounded-md border bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
        >
          Import GeoJSON
        </button>
        <button
          type="button"
          onClick={exportGeoJSON}
          className="px-3 py-1.5 text-xs font-medium rounded-md border bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
        >
          Export GeoJSON
        </button>
        <button
          type="button"
          onClick={clearAll}
          className="px-3 py-1.5 text-xs font-medium rounded-md border bg-white text-red-600 border-red-300 hover:bg-red-50"
        >
          Clear
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileImport}
          accept=".geojson,.json"
          className="hidden"
        />
      </div>

      {/* Map Container */}
      <div
        ref={mapRef}
        className="w-full rounded-md border border-gray-300"
        style={{ height: '500px' }}
      />

      <p className="mt-2 text-xs text-gray-500">
        {onReshape
          ? 'Draw roughly over what you mean: the area is merged into the shape or cut out of it, and the outline is tidied afterwards. Drag a vertex for fine adjustments. '
          : 'Click "Draw Polygon" to draw a boundary, or import a GeoJSON file. Drag a vertex to move it. '}
        {referenceLabel}
      </p>
    </div>
  );
}
